import {
  BarChart3,
  Calendar,
  Edit3,
  FileText,
  Plus,
  Save,
  Trash2,
  TrendingUp,
} from "lucide-react";
import React, { useState } from "react";
import { COMMON_LAB_TESTS } from "../../services/labResults.service";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import { LabResult } from "../../types/fhir-health";
import { LabResultsChart, labTestLabel } from "../charts/LabResultsChart";

interface LabResultsProps {
  labResults: LabResult[];
  onSave: (labResults: LabResult[]) => void;
  isEditing: boolean;
  onToggleEdit: () => void;
  isSaving: boolean;
  onFileUpload?: (file: File, labResultId?: string) => void;
}

export const LabResults: React.FC<LabResultsProps> = ({
  labResults,
  onSave,
  isEditing,
  onToggleEdit,
  isSaving,
  onFileUpload,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [localLabResults, setLocalLabResults] =
    useState<LabResult[]>(labResults);
  const [showChart, setShowChart] = useState(false);
  const [selectedTestFilter, setSelectedTestFilter] = useState<string>("all");
  const labFieldClassName =
    "w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent";

  React.useEffect(() => {
    setLocalLabResults(labResults);
  }, [labResults]);

  const addNewLabResult = () => {
    const newLabResult: LabResult = {
      id: crypto.randomUUID(),
      code: "",
      name: "",
      category: "laboratory",
      effectiveDateTime: new Date().toISOString().split("T")[0] || "",
      interpretation: "normal",
    };
    setLocalLabResults([...localLabResults, newLabResult]);
  };

  const updateLabResult = (
    index: number,
    field: keyof LabResult,
    value: string | number | Date,
  ) => {
    const updated = [...localLabResults];
    const current = updated[index];

    if (field === "code") {
      const test = COMMON_LAB_TESTS.find((t) => t.code === value);
      if (test && current) {
        updated[index] = {
          ...current,
          code: value,
          name: test.name,
          category: test.category,
          valueQuantity: current.valueQuantity
            ? {
                ...current.valueQuantity,
                unit: test.unit,
              }
            : undefined,
        } as LabResult;
      } else if (current) {
        updated[index] = { ...current, [field]: value } as LabResult;
      }
    } else if (current) {
      updated[index] = { ...current, [field]: value } as LabResult;
    }
    setLocalLabResults(updated);
  };

  const updateValueQuantity = (
    index: number,
    field: "value" | "unit",
    value: string | number,
  ) => {
    const updated = [...localLabResults];
    const current = updated[index];
    if (current) {
      updated[index] = {
        ...current,
        valueQuantity: {
          ...current.valueQuantity,
          [field]:
            field === "value" ? parseFloat(String(value)) || 0 : String(value),
          system: "http://unitsofmeasure.org",
        },
      } as LabResult;
      setLocalLabResults(updated);
    }
  };

  const removeLabResult = (index: number) => {
    const updated = localLabResults.filter((_, i) => i !== index);
    setLocalLabResults(updated);
  };

  const handleSave = () => {
    onSave(localLabResults);
  };

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    labResultId?: string,
  ) => {
    const file = event.target.files?.[0];
    if (file && file.type === "application/pdf" && onFileUpload) {
      onFileUpload(file, labResultId);
    }
  };

  // Lab units (mg/dL, %, g/dL) are universal; only the decimal separator follows
  // the app language.
  const formatLabValue = (value?: number) =>
    typeof value === "number" ? fmt.number(value, 2) : "";

  const getInterpretationColor = (interpretation?: string) => {
    switch (interpretation) {
      case "normal":
        return "text-green-600 bg-green-50";
      case "high":
        return "text-red-600 bg-red-50";
      case "low":
        return "text-primary-600 bg-primary-50";
      case "critical":
        return "text-red-800 bg-red-100";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getInterpretationIcon = (interpretation?: string) => {
    switch (interpretation) {
      case "high":
      case "critical":
        return <TrendingUp className="w-3 h-3" />;
      case "low":
        return <TrendingUp className="w-3 h-3 rotate-180" />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
          <FileText className="w-5 h-5 text-primary-600" />
          <span>{t.labResults}</span>
        </h2>
        <div className="flex space-x-2">
          {!isEditing && labResults.length > 0 && (
            <>
              <select
                value={selectedTestFilter}
                onChange={(e) => setSelectedTestFilter(e.target.value)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">{t.allTests}</option>
                {COMMON_LAB_TESTS.map((test) => {
                  const hasData = labResults.some(
                    (lr) => lr.code === test.code,
                  );
                  if (!hasData) return null;
                  return (
                    <option key={test.code} value={test.code}>
                      {labTestLabel(t, test)}
                    </option>
                  );
                }).filter(Boolean)}
              </select>
              <button
                onClick={() => setShowChart(!showChart)}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title={showChart ? t.hideChart : t.showChart}
                aria-label={showChart ? t.hideChart : t.showChart}
              >
                <BarChart3 className="w-4 h-4" />
              </button>
            </>
          )}
          <button
            onClick={onToggleEdit}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title={isEditing ? t.cancel : t.edit}
            aria-label={isEditing ? t.cancel : t.edit}
          >
            <Edit3 className="w-4 h-4" />
          </button>
          {isEditing && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              ) : (
                <Save className="w-3 h-3" />
              )}
              <span>{isSaving ? t.saving : t.save}</span>
            </button>
          )}
        </div>
      </div>

      {/* Recent Values Summary - Show when not editing */}
      {!isEditing && labResults.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            {t.currentValues}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {COMMON_LAB_TESTS.map((test) => {
              const recentResult = labResults
                .filter((lr) => lr.code === test.code)
                .sort(
                  (a, b) =>
                    new Date(b.effectiveDateTime).getTime() -
                    new Date(a.effectiveDateTime).getTime(),
                )[0];

              if (!recentResult || !recentResult.valueQuantity) return null;

              const interpretationColor = getInterpretationColor(
                recentResult.interpretation,
              );
              const interpretationIcon = getInterpretationIcon(
                recentResult.interpretation,
              );

              return (
                <div
                  key={test.code}
                  className="bg-white rounded-lg p-3 border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-gray-700">
                      {labTestLabel(t, test)}
                    </h4>
                    <div
                      className={`px-2 py-1 rounded text-xs font-medium flex items-center space-x-1 ${interpretationColor}`}
                    >
                      {interpretationIcon}
                      <span>
                        {recentResult.interpretation === "normal"
                          ? t.normal
                          : recentResult.interpretation === "high"
                            ? t.raised
                            : recentResult.interpretation === "low"
                              ? t.decreased
                              : t.critical}
                      </span>
                    </div>
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    {formatLabValue(recentResult.valueQuantity.value)}{" "}
                    {recentResult.valueQuantity.unit}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center space-x-1">
                    <Calendar className="w-3 h-3" />
                    <span>{fmt.date(recentResult.effectiveDateTime)}</span>
                  </div>
                </div>
              );
            }).filter(Boolean)}
          </div>
        </div>
      )}

      {/* Chart Section */}
      {showChart && !isEditing && labResults.length > 0 && (
        <div className="mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-6">
              {selectedTestFilter === "all" ? (
                <>
                  {/* Show all available charts when filter is 'all' */}
                  {labResults.some((lr) => lr.code === "18262-6") && (
                    <LabResultsChart
                      labResults={labResults}
                      testFilter="LDL Cholesterol"
                    />
                  )}

                  {labResults.some((lr) => lr.code === "2093-3") && (
                    <div className="h-64 flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <div className="text-lg mb-2">📊</div>
                        <p>
                          {t.labChartComingSoon.replace(
                            "{name}",
                            t.labTestTotalCholesterol,
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {labResults.some((lr) => lr.code === "2085-9") && (
                    <div className="h-64 flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <div className="text-lg mb-2">📊</div>
                        <p>
                          {t.labChartComingSoon.replace("{name}", t.labTestHdl)}
                        </p>
                      </div>
                    </div>
                  )}

                  {labResults.some((lr) => lr.code === "33747-0") && (
                    <div className="h-64 flex items-center justify-center">
                      <div className="text-center text-gray-500">
                        <div className="text-lg mb-2">📊</div>
                        <p>
                          {t.labChartComingSoon.replace(
                            "{name}",
                            t.labTestHba1c,
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Show specific test chart when filtered */
                <LabResultsChart
                  labResults={labResults}
                  testFilter={
                    COMMON_LAB_TESTS.find(
                      (test) => test.code === selectedTestFilter,
                    )?.name || undefined
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content - Editing or Display Mode */}
      {isEditing ? (
        <div className="space-y-4">
          {localLabResults.map((labResult, index) => (
            <div
              key={labResult.id || index}
              className="border border-gray-200 rounded-lg p-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.laboratoryTest}
                  </label>
                  <select
                    value={labResult.code}
                    onChange={(e) =>
                      updateLabResult(index, "code", e.target.value)
                    }
                    className={labFieldClassName}
                  >
                    <option value="">{t.pleaseSelect}</option>
                    {COMMON_LAB_TESTS.map((test) => (
                      <option key={test.code} value={test.code}>
                        {labTestLabel(t, test)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.date}
                  </label>
                  <input
                    type="date"
                    value={labResult.effectiveDateTime.split("T")[0]}
                    onChange={(e) =>
                      updateLabResult(
                        index,
                        "effectiveDateTime",
                        e.target.value,
                      )
                    }
                    className={labFieldClassName}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.evaluation}
                  </label>
                  <select
                    value={labResult.interpretation || "normal"}
                    onChange={(e) =>
                      updateLabResult(index, "interpretation", e.target.value)
                    }
                    className={labFieldClassName}
                  >
                    <option value="normal">{t.normal}</option>
                    <option value="high">{t.raised}</option>
                    <option value="low">{t.decreased}</option>
                    <option value="critical">{t.critical}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.value}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={labResult.valueQuantity?.value || ""}
                    onChange={(e) =>
                      updateValueQuantity(index, "value", e.target.value)
                    }
                    className={labFieldClassName}
                    placeholder={t.labValuePlaceholder}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.unit}
                  </label>
                  <input
                    type="text"
                    value={labResult.valueQuantity?.unit || ""}
                    onChange={(e) =>
                      updateValueQuantity(index, "unit", e.target.value)
                    }
                    className={labFieldClassName}
                    placeholder={t.labUnitPlaceholder}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.performingDoctor}
                  </label>
                  <input
                    type="text"
                    value={labResult.performer || ""}
                    onChange={(e) =>
                      updateLabResult(index, "performer", e.target.value)
                    }
                    className={labFieldClassName}
                    placeholder={t.labPerformerPlaceholder}
                  />
                </div>
              </div>

              <div className="flex justify-between items-center mt-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">
                    {t.pdfReport}:
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, labResult.id)}
                    className="text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100"
                  />
                </div>
                <button
                  onClick={() => removeLabResult(index)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title={t.labRemove}
                  aria-label={t.labRemove}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={addNewLabResult}
            className="w-full px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t.addNewLabResult}</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {labResults.length === 0 ? (
            <p className="text-gray-500 italic">{t.noLabResultsDocumented}</p>
          ) : (
            labResults
              .filter(
                (labResult) =>
                  selectedTestFilter === "all" ||
                  labResult.code === selectedTestFilter,
              )
              .map((labResult, index) => {
                const interpretationColor = getInterpretationColor(
                  labResult.interpretation,
                );
                const interpretationIcon = getInterpretationIcon(
                  labResult.interpretation,
                );

                return (
                  <div
                    key={labResult.id || index}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">
                            {labTestLabel(t, labResult)}
                          </h4>
                          <div
                            className={`px-2 py-1 rounded text-xs font-medium flex items-center space-x-1 ${interpretationColor}`}
                          >
                            {interpretationIcon}
                            <span>
                              {labResult.interpretation === "normal"
                                ? t.normal
                                : labResult.interpretation === "high"
                                  ? t.raised
                                  : labResult.interpretation === "low"
                                    ? t.decreased
                                    : t.critical}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-2 text-sm text-gray-600">
                          <div className="flex items-center space-x-4">
                            <span className="flex items-center space-x-1">
                              <Calendar className="w-3 h-3" />
                              <span>
                                {fmt.date(labResult.effectiveDateTime)}
                              </span>
                            </span>
                            {labResult.performer && (
                              <span>
                                {t.labPerformedBy.replace(
                                  "{name}",
                                  labResult.performer,
                                )}
                              </span>
                            )}
                          </div>

                          {labResult.valueQuantity && (
                            <div className="font-medium text-gray-900">
                              {formatLabValue(labResult.valueQuantity.value)}{" "}
                              {labResult.valueQuantity.unit}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
          )}
        </div>
      )}
    </div>
  );
};
